/**
 * CommunityPage - Main community hub page
 * BP-09: Community Features
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  MessageSquare,
  UserCircle,
  Heart,
  Search,
  TrendingUp,
  Plus,
  ChevronRight,
  Clock,
  Tag,
  MessageCircle,
  UserCheck,
  Calendar,
  Star,
  BookOpen,
} from 'lucide-react';
import { useFamilyContext } from '@/features/family/hooks/useFamilyContext';
import { NoFamilyState } from '@/features/family/components/NoFamilyState';
import { useForumCategories, useRecentActivity, useForumPosts } from '../hooks/useForums';
import { useMyMatches } from '../hooks/useMentors';
import { useSupportGroups } from '../hooks/useSupportGroups';
import { CommunitySearch } from '../components/CommunitySearch';

export default function CommunityPage() {
  const { familyId, isLoading: isFamilyLoading, hasFamily } = useFamilyContext();
  const [searchQuery, setSearchQuery] = useState('');

  // Queries
  const { data: categoriesData, isLoading: isLoadingCategories } = useForumCategories();
  const { data: recentActivity, isLoading: isLoadingActivity } = useRecentActivity();
  const { data: postsData, isLoading: isLoadingPosts } = useForumPosts({ limit: 5 });
  const { data: mentorData } = useMyMatches();
  const { data: groupsData } = useSupportGroups();

  // Computed
  const categories = categoriesData?.categories ?? [];
  const posts = postsData?.items ?? [];
  const mentorMatches = mentorData?.matches ?? [];
  const supportGroups = groupsData?.items ?? [];

  // Format date helper
  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Loading state
  if (isFamilyLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
      </div>
    );
  }

  // No family state
  if (!hasFamily || !familyId) {
    return <NoFamilyState />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-navy">Community</h1>
          <p className="text-warm-gray mt-1">
            Connect with other families, find mentors, and join support groups
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/family/community/forums/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Post
          </Link>
        </div>
      </div>

      {/* Search */}
      <CommunitySearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search forums, mentors, and groups..."
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-sage" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-navy">{categories.length}</p>
              <p className="text-sm text-warm-gray">Forum Categories</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <UserCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-navy">{mentorMatches.length}</p>
              <p className="text-sm text-warm-gray">Mentor Matches</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-terracotta/10 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-terracotta" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-navy">{supportGroups.length}</p>
              <p className="text-sm text-warm-gray">Support Groups</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-navy">{posts.length}</p>
              <p className="text-sm text-warm-gray">Recent Posts</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Forum Categories */}
        <div className="lg:col-span-2 space-y-6">
          {/* Categories */}
          <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
            <div className="p-4 border-b border-warm-gray/10 flex items-center justify-between">
              <h2 className="font-semibold text-navy flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-sage" />
                Forum Categories
              </h2>
              <Link
                to="/family/community/forums"
                className="text-sm text-sage hover:underline flex items-center gap-1"
              >
                View All
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {isLoadingCategories ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sage" />
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-10 h-10 text-warm-gray/50 mx-auto mb-2" />
                <p className="text-warm-gray">No forum categories yet</p>
              </div>
            ) : (
              <div className="divide-y divide-warm-gray/10">
                {categories.slice(0, 5).map((category) => (
                  <Link
                    key={category.id}
                    to={`/family/community/forums/${category.slug}`}
                    className="flex items-start gap-4 p-4 hover:bg-cream/50 transition-colors"
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <MessageSquare
                        className="w-5 h-5"
                        style={{ color: category.color }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-navy">{category.name}</h3>
                      <p className="text-sm text-warm-gray line-clamp-1">
                        {category.description}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-warm-gray">
                        <span>{category.post_count} posts</span>
                        <span>•</span>
                        <span>{category.reply_count} replies</span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-warm-gray flex-shrink-0" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Recent Posts */}
          <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
            <div className="p-4 border-b border-warm-gray/10 flex items-center justify-between">
              <h2 className="font-semibold text-navy flex items-center gap-2">
                <Clock className="w-5 h-5 text-sage" />
                Recent Discussions
              </h2>
              <Link
                to="/family/community/forums"
                className="text-sm text-sage hover:underline flex items-center gap-1"
              >
                View All
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {isLoadingPosts ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sage" />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircle className="w-10 h-10 text-warm-gray/50 mx-auto mb-2" />
                <p className="text-warm-gray">No posts yet</p>
                <Link
                  to="/family/community/forums/new"
                  className="inline-flex items-center gap-2 mt-3 text-sage hover:underline"
                >
                  <Plus className="w-4 h-4" />
                  Start a discussion
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-warm-gray/10">
                {posts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/family/community/forums/post/${post.id}`}
                    className="block p-4 hover:bg-cream/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-sage/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-sage">
                          {post.author?.first_name?.[0] ?? 'U'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-navy line-clamp-1">{post.title}</h3>
                        <p className="text-sm text-warm-gray line-clamp-2 mt-1">
                          {post.preview}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-warm-gray">
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {post.reply_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {post.like_count}
                          </span>
                          <span>{formatRelativeTime(post.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Mentor Matches */}
          <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
            <div className="p-4 border-b border-warm-gray/10 flex items-center justify-between">
              <h2 className="font-semibold text-navy flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-sage" />
                Find a Mentor
              </h2>
              <Link
                to="/family/community/mentors"
                className="text-sm text-sage hover:underline"
              >
                Browse
              </Link>
            </div>
            <div className="p-4">
              <p className="text-sm text-warm-gray mb-4">
                Connect with experienced parents who understand your journey.
              </p>
              <Link
                to="/family/community/mentors/find"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-sage/10 text-sage rounded-lg hover:bg-sage/20 transition-colors"
              >
                <UserCircle className="w-4 h-4" />
                Find Your Match
              </Link>
            </div>
          </div>

          {/* Support Groups */}
          <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
            <div className="p-4 border-b border-warm-gray/10 flex items-center justify-between">
              <h2 className="font-semibold text-navy flex items-center gap-2">
                <Users className="w-5 h-5 text-sage" />
                Support Groups
              </h2>
              <Link
                to="/family/community/groups"
                className="text-sm text-sage hover:underline"
              >
                View All
              </Link>
            </div>
            <div className="divide-y divide-warm-gray/10">
              {supportGroups.slice(0, 3).map((group) => (
                <Link
                  key={group.id}
                  to={`/family/community/groups/${group.id}`}
                  className="flex items-center gap-3 p-3 hover:bg-cream/50 transition-colors"
                >
                  <div className="w-8 h-8 bg-terracotta/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-terracotta" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-navy truncate">{group.name}</h3>
                    <p className="text-xs text-warm-gray">{group.member_count} members</p>
                  </div>
                </Link>
              ))}
              {supportGroups.length === 0 && (
                <div className="p-4 text-center text-sm text-warm-gray">
                  No groups available yet
                </div>
              )}
            </div>
          </div>

          {/* Community Guidelines */}
          <div className="bg-cream/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-navy" />
              <h3 className="font-semibold text-navy">Community Guidelines</h3>
            </div>
            <ul className="text-sm text-warm-gray space-y-2">
              <li className="flex items-start gap-2">
                <Star className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" />
                <span>Be respectful and supportive</span>
              </li>
              <li className="flex items-start gap-2">
                <Star className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" />
                <span>Share experiences, not medical advice</span>
              </li>
              <li className="flex items-start gap-2">
                <Star className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" />
                <span>Protect privacy - no personal info</span>
              </li>
              <li className="flex items-start gap-2">
                <Star className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" />
                <span>Report concerns to moderators</span>
              </li>
            </ul>
          </div>

          {/* Upcoming Events */}
          <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
            <div className="p-4 border-b border-warm-gray/10 flex items-center justify-between">
              <h2 className="font-semibold text-navy flex items-center gap-2">
                <Calendar className="w-5 h-5 text-sage" />
                Upcoming Events
              </h2>
            </div>
            <div className="p-4 text-center">
              <Calendar className="w-10 h-10 text-warm-gray/50 mx-auto mb-2" />
              <p className="text-sm text-warm-gray">No upcoming events</p>
              <p className="text-xs text-warm-gray mt-1">Check back soon!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
